import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Archive, ArrowLeft, Briefcase, CheckCircle2, Circle, Plus, RotateCcw, Save, Search, SlidersHorizontal, Sparkles, Star, Target, Trash2, Users, AlertTriangle } from "lucide-react";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../utils/api";
import { actionFailureMessage, useLongActionNotice } from "../utils/actionFeedback";
import DashboardLayout from "../components/layout/DashboardLayout";

const makeCriteriaRowId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const normalizeCriteriaSkill = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item.trim();
  return String(item.skill || item.name || "").trim();
};

const buildCriteriaRowsFromJob = (job) => {
  const rows = [];

  (job?.must_have_skills || []).forEach((item) => {
    const skill = normalizeCriteriaSkill(item);
    if (!skill) return;

    rows.push({
      id: makeCriteriaRowId(),
      skill,
      type: "must",
      threshold: Number(item?.threshold || job?.skill_thresholds?.[skill] || 70),
      reason: item?.reason || "",
    });
  });

  (job?.preferred_skills || []).forEach((item) => {
    const skill = normalizeCriteriaSkill(item);
    if (!skill) return;

    rows.push({
      id: makeCriteriaRowId(),
      skill,
      type: "preferred",
      threshold: 40,
      reason: item?.reason || "",
    });
  });

  return rows;
};

export default function JobDetails() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const rediscoveryRef = useRef(null);

  const [jobData, setJobData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [query, setQuery] = useState("");
  const [scansLeft, setScansLeft] = useState(null);
  const [updatingArchive, setUpdatingArchive] = useState(false);
  const [resettingScans, setResettingScans] = useState(false);
  const [savingCriteria, setSavingCriteria] = useState(false);
  const [detectingCriteria, setDetectingCriteria] = useState(false);
  const [criteriaRows, setCriteriaRows] = useState([]);
  const [criteriaSourceExcerpt, setCriteriaSourceExcerpt] = useState("");
  const [jobIntakeAgentSummary, setJobIntakeAgentSummary] = useState("");
  const [jobIntakeWarnings, setJobIntakeWarnings] = useState([]);
  const [detectedLanguageRequirements, setDetectedLanguageRequirements] = useState([]);
  const [detectedEducationRequirements, setDetectedEducationRequirements] = useState([]);
  const [rediscoveryData, setRediscoveryData] = useState(null);
  const [rediscovering, setRediscovering] = useState(false);
  const [selectedRediscoveryIds, setSelectedRediscoveryIds] = useState([]);
  const [linkingRediscovery, setLinkingRediscovery] = useState(false);
  const [criteriaForm, setCriteriaForm] = useState({
    mustHaveText: "",
    preferredText: "",
  });

  useLongActionNotice(rediscovering, "AI Pool Matches", 45000);
  useLongActionNotice(updatingArchive, "Job status update", 30000);
  useLongActionNotice(resettingScans, "Reset scan results", 30000);
  useLongActionNotice(detectingCriteria, "Job Intake Agent", 45000);
  useLongActionNotice(savingCriteria, "Save scoring criteria", 30000);
  useLongActionNotice(linkingRediscovery, "Add AI Pool Matches", 30000);

  const loadRediscoveredCandidates = async () => {
    if (!user || !jobId || user?.plan === "free") return;

    setRediscovering(true);

    try {
      const res = await apiFetch(`/jobs/${jobId}/rediscover-candidates?limit=12&min_score=35`);
      const data = await res.json();

      if (!res.ok) {
        setRediscoveryData(null);
        return;
      }

      setRediscoveryData(data);
      setSelectedRediscoveryIds([]);
    } catch (err) {
      setRediscoveryData(null);
    } finally {
      setRediscovering(false);
    }
  };

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user || !jobId) return;

    const fetchJobDetails = async () => {
      setFetching(true);

      try {
        const [jobRes, dashboardRes] = await Promise.all([
          apiFetch(`/jobs/${jobId}/details`),
          apiFetch("/dashboard/my_scans/"),
        ]);
        const data = await jobRes.json();

        if (!jobRes.ok) {
          toast.error(data?.detail || "Failed to load job details.");
          navigate("/dashboard_recruiter");
          return;
        }

        setJobData(data);
        if (user?.plan !== "free") {
          loadRediscoveredCandidates();
        }

        if (dashboardRes.ok) {
          const dashboardData = await dashboardRes.json();
          setScansLeft(dashboardData.scans_left);
        }
      } catch (err) {
        toast.error("Failed to load job details.");
      } finally {
        setFetching(false);
      }
    };

    fetchJobDetails();
  }, [user, jobId, navigate]);

  useEffect(() => {
    const job = jobData?.job;
    if (!job) return;

    const mustHaveText = (job.must_have_skills || [])
      .map((item) => {
        if (typeof item === "string") {
          const threshold = job.skill_thresholds?.[item];
          return threshold ? `${item}: ${threshold}` : item;
        }

        return `${item.skill || item.name || ""}: ${item.threshold || job.skill_thresholds?.[item.skill] || 70}`;
      })
      .filter(Boolean)
      .join("\n");

    const preferredText = (job.preferred_skills || [])
      .map((item) => typeof item === "string" ? item : item.skill || item.name || "")
      .filter(Boolean)
      .join(", ");

    setCriteriaForm({ mustHaveText, preferredText });
    setCriteriaRows(buildCriteriaRowsFromJob(job));
  }, [jobData?.job]);

  useEffect(() => {
    if (fetching || searchParams.get("focus") !== "rediscovery") return;

    window.setTimeout(() => {
      rediscoveryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  }, [fetching, searchParams]);

  const filteredCandidates = useMemo(() => {
    const candidates = jobData?.candidates || [];
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) return candidates;

    // Keep filtering local and lightweight: this is for quick scanning within
    // the already-loaded job results, while Candidate Search remains the
    // semantic/RAG search surface.
    return candidates.filter((candidate) => {
      const haystack = [
        candidate.filename,
        ...(candidate.matching_skills || []),
        ...(candidate.missing_skills || []),
        candidate.recruiter_note,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [jobData, query]);

  if (loading || fetching || !user) {
    return <p className="text-center mt-20">Loading...</p>;
  }

  const job = jobData?.job || {};
  const stats = jobData?.stats || {};
  const isArchived = Boolean(job.is_archived);
  const isFree = user?.plan === "free";
  const canResetScanResults = ["pro", "enterprise"].includes((user?.plan || "").toLowerCase());
  const quotaExceeded = scansLeft !== null && scansLeft !== undefined && scansLeft <= 0;
  const bulkScanLocked = isFree || quotaExceeded || isArchived;
  const hasScannedCandidates = (stats.total_candidates || 0) > 0;
  const criteriaSimulatorLocked = !hasScannedCandidates;
  const pipeline = jobData?.pipeline || {
    shortlisted: [],
    strong_matches: [],
    review: [],
  };

  const handleOpenRankings = () => {
    if (isFree) {
      toast.error("Upgrade to Basic or Pro to use Rankings.");
      return;
    }

    navigate(`/jobs/${jobId}/rankings`);
  };

  const handleOpenCriteriaSimulator = () => {
    if (isFree) {
      toast.error("Upgrade to Basic or Pro to use Criteria Match.");
      return;
    }

    if (criteriaSimulatorLocked) {
      toast.error("Run a scan for this job first. Criteria Simulator becomes available after scanned candidate results exist.");
      return;
    }

    navigate(`/jobs/${jobId}/criteria-simulator`);
  };

  const readinessItems = [
    {
      label: "Job description",
      done: Boolean((job.description || "").trim()),
      action: "Saved",
    },
    {
      label: "Scoring criteria",
      done: criteriaRows.some((row) => row.skill?.trim()),
      action: "Run intake agent or add criteria",
      onClick: () => document.getElementById("job-scoring-criteria")?.scrollIntoView({ behavior: "smooth", block: "start" }),
    },
    {
      label: "Applicants linked",
      done: (stats.application_count || 0) > 0,
      action: "Add candidates",
      onClick: () => navigate(`/candidates?job_id=${jobId}&scope=job`),
    },
    {
      label: "Candidates scanned",
      done: (stats.total_candidates || 0) > 0,
      action: "Open Bulk Scan",
      onClick: () => navigate(`/bulk-scan?job_id=${jobId}`),
    },
    {
      label: "Shortlist reviewed",
      done: (stats.shortlisted || 0) > 0,
      action: "Open Rankings",
      onClick: handleOpenRankings,
    },
  ];

  const leftPanel = (
    <div className="flex flex-col gap-4">
      <h3 className="mb-1 px-3 text-xs font-semibold uppercase tracking-wide text-slate-400">
        Job Workspace
      </h3>

      <div className="grid gap-1">
        <p className="px-3 text-[10px] font-semibold uppercase tracking-wide text-emerald-300/70">
          Navigation
        </p>
      <button
        onClick={() => navigate("/dashboard_recruiter")}
        className="flex min-h-[34px] items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-emerald-100/80 transition hover:bg-white/10 hover:text-white"
      >
        <ArrowLeft size={16} />
        Dashboard
      </button>

      <button
        onClick={() => navigate("/jobs")}
        className="flex min-h-[34px] items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-emerald-100/80 transition hover:bg-white/10 hover:text-white"
      >
        <Briefcase size={16} />
        All Jobs
      </button>
      </div>

      <div className="grid gap-1">
        <p className="px-3 text-[10px] font-semibold uppercase tracking-wide text-emerald-300/70">
          Review
        </p>
      <button
        onClick={handleOpenRankings}
        aria-disabled={isFree}
        title={isFree ? "Available on Basic and Pro plans." : "Open job rankings."}
        className={`flex min-h-[34px] items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] font-medium transition ${
          isFree
            ? "cursor-not-allowed text-slate-500"
            : "text-emerald-100/80 hover:bg-white/10 hover:text-white"
        }`}
      >
        <Star size={16} />
        Rankings
      </button>

      <button
        onClick={handleOpenCriteriaSimulator}
        aria-disabled={isFree || criteriaSimulatorLocked}
        title={isFree ? "Available on Basic and Pro plans." : criteriaSimulatorLocked ? "Run a scan for this job first." : "Open Criteria Match."}
        className={`flex min-h-[34px] items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] font-medium transition ${
          isFree || criteriaSimulatorLocked
            ? "cursor-not-allowed text-slate-500"
            : "text-emerald-100/80 hover:bg-white/10 hover:text-white"
        }`}
      >
        <Target size={16} />
        Criteria Simulator
      </button>
      </div>

      <div className="grid gap-1">
        <p className="px-3 text-[10px] font-semibold uppercase tracking-wide text-emerald-300/70">
          Actions
        </p>
      <button
        onClick={() => {
          if (isFree) {
            toast.error("Candidates is available on Basic and Pro plans.");
            return;
          }
          if (isArchived) {
            toast.error("Restore this job before importing more candidates.");
            return;
          }

          navigate(`/candidates?job_id=${jobId}`);
        }}
        aria-disabled={isFree || isArchived}
        className={`flex min-h-[34px] items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] font-medium transition ${
          isFree || isArchived
            ? "cursor-not-allowed text-slate-500"
            : "text-emerald-100/80 hover:bg-white/10 hover:text-white"
        }`}
      >
        <Users size={16} />
        Candidates
      </button>

      <button
        onClick={() => {
          if (bulkScanLocked) {
            toast.error(
              isArchived
                ? "Restore this job before scanning candidates."
                : isFree
                ? "Bulk Scan is available on Basic and Pro plans."
                : "Your scan quota is finished for this billing cycle."
            );
            return;
          }

          navigate(`/bulk-scan?job_id=${jobId}`);
        }}
        aria-disabled={bulkScanLocked}
        className={`flex min-h-[34px] items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] font-medium transition ${
          bulkScanLocked
            ? "cursor-not-allowed text-slate-500"
            : "text-emerald-100/80 hover:bg-white/10 hover:text-white"
        }`}
      >
        <Search size={16} />
        Bulk Scan
      </button>
      </div>

    </div>
  );

  const scoreClass = (score) => {
    if (score >= 75) return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (score >= 55) return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-rose-50 text-rose-700 border-rose-200";
  };

  const formatDate = (value) => {
    if (!value) return "Not available";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleArchiveToggle = async () => {
    const action = isArchived ? "restore" : "archive";

    if (!isArchived && !window.confirm("Archive this job? Its scan history and candidate decisions will be kept.")) {
      return;
    }

    setUpdatingArchive(true);

    try {
      const res = await apiFetch(`/jobs/${jobId}/${action}`, { method: "PATCH" });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage(`${action} job`, data?.detail));
        return;
      }

      setJobData((prev) => ({
        ...prev,
        job: {
          ...(prev?.job || {}),
          is_archived: data.is_archived,
          archived_at: data.archived_at,
        },
      }));
      toast.success(isArchived ? "Job restored." : "Job archived.");
    } catch (err) {
      toast.error(actionFailureMessage(`${action} job`));
    } finally {
      setUpdatingArchive(false);
    }
  };

  const handleResetScanResults = async () => {
    if (!canResetScanResults) {
      toast.error("Reset job scan results is available on Pro and Enterprise plans.");
      return;
    }

    const confirmation = window.prompt(
      "This clears rankings, scan history, AI analysis, notes, shortlist/reject decisions, and email status for this job only.\n\nCandidates stay attached to the job. Used scan quota will not be restored.\n\nType RESET to confirm."
    );

    if (confirmation !== "RESET") {
      if (confirmation !== null) {
        toast.error("Reset cancelled. Type RESET exactly to confirm.");
      }
      return;
    }

    setResettingScans(true);

    try {
      const res = await apiFetch(`/jobs/${jobId}/scan-results`, {
        method: "DELETE",
        body: JSON.stringify({ confirmation }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Reset scan results", data?.detail));
        return;
      }

      setJobData((prev) => ({
        ...prev,
        stats: {
          ...(prev?.stats || {}),
          total_candidates: 0,
          shortlisted: 0,
          average_score: 0,
        },
        pipeline: {
          shortlisted: [],
          strong_matches: [],
          review: [],
        },
      }));
      toast.success(`${data.deleted_scans || 0} scan result(s) cleared. Scan quota was not restored.`);
    } catch (err) {
      toast.error(actionFailureMessage("Reset scan results"));
    } finally {
      setResettingScans(false);
    }
  };

  const parseMustHaveCriteria = () => {
    const thresholds = {};

    const skills = criteriaForm.mustHaveText
      .split(/\n|,/)
      .map((raw) => raw.trim())
      .filter(Boolean)
      .map((raw) => {
        const [skillPart, thresholdPart] = raw.split(":");
        const skill = (skillPart || "").trim();
        const threshold = Math.max(0, Math.min(100, Number(thresholdPart) || 70));
        thresholds[skill] = threshold;

        return { skill, threshold };
      })
      .filter((item) => item.skill);

    return { skills, thresholds };
  };

  const parsePreferredCriteria = () =>
    criteriaForm.preferredText
      .split(/\n|,/)
      .map((skill) => skill.trim())
      .filter(Boolean)
      .map((skill) => ({ skill }));

  const updateCriteriaRow = (rowId, updates) => {
    setCriteriaRows((prev) => prev.map((row) => (
      row.id === rowId ? { ...row, ...updates } : row
    )));
  };

  const removeCriteriaRow = (rowId) => {
    setCriteriaRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  const addCriteriaRow = (type = "must") => {
    setCriteriaRows((prev) => [
      ...prev,
      {
        id: makeCriteriaRowId(),
        skill: "",
        type,
        threshold: type === "must" ? 70 : 40,
        reason: "",
      },
    ]);
  };

  const getCriteriaPayloadFromRows = () => {
    const mustHaveSkills = criteriaRows
      .filter((row) => row.type === "must" && row.skill.trim())
      .map((row) => ({
        skill: row.skill.trim(),
        threshold: Math.max(0, Math.min(100, Number(row.threshold) || 70)),
        reason: row.reason || "",
      }));
    const preferredSkills = criteriaRows
      .filter((row) => row.type === "preferred" && row.skill.trim())
      .map((row) => ({
        skill: row.skill.trim(),
        reason: row.reason || "",
      }));
    const thresholds = mustHaveSkills.reduce((acc, item) => {
      acc[item.skill] = item.threshold;
      return acc;
    }, {});

    return { mustHaveSkills, preferredSkills, thresholds };
  };

  const syncCriteriaFormFromRows = (rows) => {
    const mustHaveText = rows
      .filter((row) => row.type === "must" && row.skill.trim())
      .map((row) => `${row.skill.trim()}: ${Math.max(0, Math.min(100, Number(row.threshold) || 70))}`)
      .join("\n");
    const preferredText = rows
      .filter((row) => row.type === "preferred" && row.skill.trim())
      .map((row) => row.skill.trim())
      .join(", ");

    setCriteriaForm({ mustHaveText, preferredText });
  };

  const handleDetectCriteria = async () => {
    if (isFree) {
      toast.error("Upgrade to Basic or Pro to use the Job Intake Agent.");
      return;
    }

    if (isArchived) {
      toast.error("Restore this job before detecting criteria.");
      return;
    }

    setDetectingCriteria(true);

    try {
      const res = await apiFetch(`/jobs/${jobId}/detect-scoring-criteria`, {
        method: "POST",
        body: JSON.stringify({ description: job.description || "" }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Job Intake Agent", data?.detail));
        return;
      }

      const detectedRows = [
        ...(data.must_have_skills || []).map((item) => ({
          id: makeCriteriaRowId(),
          skill: item.skill || "",
          type: "must",
          threshold: Math.max(0, Math.min(100, Number(item.threshold) || 70)),
          reason: item.reason || "",
        })),
        ...(data.preferred_skills || []).map((item) => ({
          id: makeCriteriaRowId(),
          skill: item.skill || "",
          type: "preferred",
          threshold: 40,
          reason: item.reason || "",
        })),
      ].filter((row) => row.skill);

      setCriteriaRows(detectedRows);
      syncCriteriaFormFromRows(detectedRows);
      setCriteriaSourceExcerpt(data.source_excerpt || "");
      setJobIntakeAgentSummary(data.agent_summary || "");
      setJobIntakeWarnings(data.intake_warnings || []);
      setDetectedLanguageRequirements(data.language_requirements || []);
      setDetectedEducationRequirements(data.education_requirements || []);
      toast.success("Job Intake Agent prepared criteria. Review the sliders before saving.");
    } catch (err) {
      toast.error(actionFailureMessage("Job Intake Agent"));
    } finally {
      setDetectingCriteria(false);
    }
  };

  const handleSaveCriteria = async () => {
    const rowPayload = criteriaRows.length ? getCriteriaPayloadFromRows() : null;
    const { skills: fallbackMustHaveSkills, thresholds: fallbackThresholds } = parseMustHaveCriteria();
    const fallbackPreferredSkills = parsePreferredCriteria();
    const mustHaveSkills = rowPayload ? rowPayload.mustHaveSkills : fallbackMustHaveSkills;
    const preferredSkills = rowPayload ? rowPayload.preferredSkills : fallbackPreferredSkills;
    const thresholds = rowPayload ? rowPayload.thresholds : fallbackThresholds;

    setSavingCriteria(true);

    try {
      const res = await apiFetch(`/jobs/${jobId}/scoring-criteria`, {
        method: "PATCH",
        body: JSON.stringify({
          must_have_skills: mustHaveSkills,
          preferred_skills: preferredSkills,
          skill_thresholds: thresholds,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Save scoring criteria", data?.detail));
        return;
      }

      setJobData((prev) => ({
        ...prev,
        job: {
          ...(prev?.job || {}),
          must_have_skills: data.must_have_skills || [],
          preferred_skills: data.preferred_skills || [],
          skill_thresholds: data.skill_thresholds || {},
        },
      }));
      toast.success("Scoring criteria saved.");
    } catch (err) {
      toast.error(actionFailureMessage("Save scoring criteria"));
    } finally {
      setSavingCriteria(false);
    }
  };

  const toggleRediscoveryCandidate = (candidateId) => {
    setSelectedRediscoveryIds((prev) =>
      prev.includes(candidateId)
        ? prev.filter((id) => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  const toggleAllRediscoveryCandidates = () => {
    const candidateIds = (rediscoveryData?.candidates || []).map((candidate) => candidate.candidate_id);
    if (!candidateIds.length) return;

    const allSelected = candidateIds.every((candidateId) => selectedRediscoveryIds.includes(candidateId));
    setSelectedRediscoveryIds(allSelected ? [] : candidateIds);
  };

  const handleLinkRediscoveredCandidates = async () => {
    if (!selectedRediscoveryIds.length) {
      toast.error("Select at least one rediscovered candidate.");
      return;
    }

    if (isArchived) {
      toast.error("Restore this job before adding rediscovered candidates.");
      return;
    }

    setLinkingRediscovery(true);

    try {
      const res = await apiFetch(`/jobs/${jobId}/rediscover-candidates/link`, {
        method: "POST",
        body: JSON.stringify({ candidate_ids: selectedRediscoveryIds }),
      });
      const data = await res.json();

      if (!res.ok) {
        toast.error(actionFailureMessage("Add AI Pool Matches", data?.detail));
        return;
      }

      toast.success(`${data.linked_count || selectedRediscoveryIds.length} candidate(s) added to this job.`);
      navigate(`/bulk-scan?job_id=${jobId}&candidate_ids=${selectedRediscoveryIds.join(",")}&source=rediscovered`);
    } catch (err) {
      toast.error(actionFailureMessage("Add AI Pool Matches"));
    } finally {
      setLinkingRediscovery(false);
    }
  };

  const pipelineColumns = [
    {
      key: "shortlisted",
      title: "Shortlisted",
      subtitle: "Recruiter-approved candidates",
      items: pipeline.shortlisted || [],
    },
    {
      key: "strong_matches",
      title: "Strong Matches",
      subtitle: "High scoring candidates",
      items: pipeline.strong_matches || [],
    },
    {
      key: "review",
      title: "Review",
      subtitle: "Candidates still under consideration",
      items: pipeline.review || [],
    },
  ];

  const mainContent = (
    <>
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-semibold text-slate-950">Job Description</h2>
              {isArchived && (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
                  <Archive size={13} />
                  Archived
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-slate-500">
              {isArchived
                ? "This job is archived. Restore it before importing or scanning more candidates."
                : "Role requirements used for candidate matching and ranking."}
            </p>
          </div>
        </div>

        <div className="mt-5 max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="whitespace-pre-line text-sm leading-6 text-slate-700">
            {job.description || "No job description saved."}
          </p>
        </div>
      </section>

      <section className="rounded-lg border border-emerald-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Job Readiness Checklist</h2>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              A quick view of what is ready and what should happen next for this role.
            </p>
          </div>
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
            {readinessItems.filter((item) => item.done).length} / {readinessItems.length} ready
          </span>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-5">
          {readinessItems.map((item) => {
            const Icon = item.done ? CheckCircle2 : Circle;

            return (
              <button
                key={item.label}
                type="button"
                onClick={item.done ? undefined : item.onClick}
                className={`rounded-lg border p-3 text-left transition ${
                  item.done
                    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                    : "border-slate-200 bg-slate-50 text-slate-700 hover:border-emerald-200 hover:bg-emerald-50"
                }`}
              >
                <Icon size={18} className={item.done ? "text-emerald-700" : "text-slate-400"} />
                <p className="mt-3 text-sm font-semibold">{item.label}</p>
                <p className={`mt-1 text-xs ${item.done ? "text-emerald-700" : "text-slate-500"}`}>
                  {item.done ? "Complete" : item.action}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      <section id="job-scoring-criteria" className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <SlidersHorizontal size={18} className="text-emerald-700" />
              <h2 className="text-xl font-semibold text-emerald-950">Job Intake Agent</h2>
            </div>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-emerald-800">
              Let RecruitGlance read the requirements section, suggest must-have and preferred criteria, then keep the recruiter in control before saving.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handleDetectCriteria}
              disabled={detectingCriteria}
              aria-disabled={isFree || isArchived}
              title={isFree ? "Available on Basic and Pro plans." : isArchived ? "Restore this job before running the intake agent." : "Run the Job Intake Agent on this job description."}
              className={`inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border px-4 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                isFree || isArchived
                  ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                  : "border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100"
              }`}
            >
              <Sparkles size={16} />
              {detectingCriteria ? "Reading JD..." : "Run Job Intake Agent"}
            </button>

            <button
              type="button"
              onClick={handleSaveCriteria}
              disabled={savingCriteria || isArchived}
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-emerald-800 px-4 text-sm font-semibold text-white transition hover:bg-emerald-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Save size={16} />
              {savingCriteria ? "Saving..." : "Save Criteria"}
            </button>
          </div>
        </div>

        <div className="mt-5 rounded-lg border border-emerald-200 bg-white p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-emerald-950">Editable criteria builder</h3>
              <p className="mt-1 text-sm leading-6 text-emerald-700">
                The agent gives a starting point from the requirements/profile part of the JD. Adjust the score bars before saving.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => addCriteriaRow("must")}
                disabled={isArchived}
                className="inline-flex min-h-[36px] items-center justify-center gap-2 rounded-lg border border-emerald-200 px-3 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus size={14} />
                Must-have
              </button>
              <button
                type="button"
                onClick={() => addCriteriaRow("preferred")}
                disabled={isArchived}
                className="inline-flex min-h-[36px] items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus size={14} />
                Preferred
              </button>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {criteriaRows.length === 0 ? (
              <div className="rounded-lg border border-dashed border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
                No scoring criteria saved yet. Run the Job Intake Agent or add a must-have skill manually.
              </div>
            ) : (
              criteriaRows.map((row) => (
                <div key={row.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="grid gap-3 lg:grid-cols-[minmax(160px,1fr)_150px_minmax(220px,1fr)_42px] lg:items-center">
                    <input
                      value={row.skill}
                      onChange={(event) => updateCriteriaRow(row.id, { skill: event.target.value })}
                      disabled={isArchived}
                      placeholder="Skill name"
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-400"
                    />

                    <select
                      value={row.type}
                      onChange={(event) => updateCriteriaRow(row.id, {
                        type: event.target.value,
                        threshold: event.target.value === "must" ? row.threshold || 70 : 40,
                      })}
                      disabled={isArchived}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-400"
                    >
                      <option value="must">Must-have</option>
                      <option value="preferred">Preferred</option>
                    </select>

                    {row.type === "must" ? (
                      <div>
                        <div className="flex items-center gap-3">
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={row.threshold}
                            onChange={(event) => updateCriteriaRow(row.id, { threshold: Number(event.target.value) })}
                            disabled={isArchived}
                            className="w-full accent-emerald-700"
                          />
                          <span className="w-12 text-right text-sm font-bold text-emerald-900">{row.threshold}%</span>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">Minimum evidence expected for this skill.</p>
                      </div>
                    ) : (
                      <p className="rounded-lg bg-white px-3 py-2 text-sm text-slate-500">
                        Bonus signal. It improves ranking but does not block candidates.
                      </p>
                    )}

                    <button
                      type="button"
                      onClick={() => removeCriteriaRow(row.id)}
                      disabled={isArchived}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label="Remove criterion"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  {row.reason && (
                    <p className="mt-3 rounded-md bg-white px-3 py-2 text-xs leading-5 text-slate-500">
                      AI reason: {row.reason}
                    </p>
                  )}
                </div>
              ))
            )}
          </div>

          {(jobIntakeAgentSummary || jobIntakeWarnings.length > 0) && (
            <div className="mt-4 grid gap-3 lg:grid-cols-[1.5fr_1fr]">
              {jobIntakeAgentSummary && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-emerald-950">
                    <Sparkles size={16} className="text-emerald-700" />
                    Agent recommendation
                  </div>
                  <p className="mt-2 text-sm leading-6 text-emerald-800">{jobIntakeAgentSummary}</p>
                </div>
              )}

              {jobIntakeWarnings.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                    <AlertTriangle size={16} />
                    Review before saving
                  </div>
                  <ul className="mt-2 space-y-1 text-sm leading-6 text-amber-800">
                    {jobIntakeWarnings.map((item, index) => (
                      <li key={`${item.warning || item.text || "warning"}-${index}`}>
                        {item.warning || item.text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {(detectedLanguageRequirements.length > 0 || detectedEducationRequirements.length > 0 || criteriaSourceExcerpt) && (
            <div className="mt-4 grid gap-3 lg:grid-cols-3">
              {detectedLanguageRequirements.length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Language requirements</p>
                  <p className="mt-2 text-sm text-slate-700">
                    {detectedLanguageRequirements.map((item) => `${item.language || "Language"} ${item.level || ""}`.trim()).join(", ")}
                  </p>
                </div>
              )}
              {detectedEducationRequirements.length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Education requirements</p>
                  <p className="mt-2 text-sm text-slate-700">
                    {detectedEducationRequirements.map((item) => item.qualification || item.name || "Education requirement").join(", ")}
                  </p>
                </div>
              )}
              {criteriaSourceExcerpt && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 lg:col-span-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">JD section used</p>
                  <p className="mt-2 line-clamp-4 text-sm leading-6 text-slate-700">{criteriaSourceExcerpt}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        {[
          { label: "Candidates", value: stats.total_candidates || 0, icon: Users },
          { label: "Shortlisted", value: stats.shortlisted || 0, icon: Star },
          { label: "Strong Matches", value: stats.strong_matches || 0, icon: Briefcase },
          { label: "Avg. Score", value: `${stats.average_score || 0}%`, icon: Search },
        ].map((item) => {
          const Icon = item.icon;

          return (
            <div key={item.label} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-slate-500">{item.label}</p>
                <Icon size={18} className="text-slate-400" />
              </div>
              <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{item.value}</p>
            </div>
          );
        })}
      </section>

      <section ref={rediscoveryRef} className="rounded-lg border border-emerald-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="text-emerald-700" />
              <h2 className="text-xl font-semibold text-slate-950">Candidate Re-discovery</h2>
            </div>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-500">
              Before sourcing more CVs, check whether your existing candidate pool already contains people who may fit this role.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={loadRediscoveredCandidates}
              disabled={rediscovering || isFree}
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Search size={16} />
              {rediscovering ? "Checking pool..." : "Refresh Matches"}
            </button>
            <button
              type="button"
              onClick={handleLinkRediscoveredCandidates}
              disabled={isFree || isArchived || linkingRediscovery || selectedRediscoveryIds.length === 0}
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg bg-emerald-800 px-4 text-sm font-semibold text-white transition hover:bg-emerald-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus size={16} />
              {linkingRediscovery ? "Adding..." : `Add & Scan (${selectedRediscoveryIds.length})`}
            </button>
          </div>
        </div>

        {isFree ? (
          <div className="mt-5 rounded-lg border border-dashed border-emerald-200 bg-emerald-50 p-5 text-sm leading-6 text-emerald-800">
            Candidate Re-discovery is available on paid plans. It searches your saved candidate pool for possible matches before you upload more CVs.
          </div>
        ) : rediscovering ? (
          <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-5 text-sm font-medium text-slate-600">
            Checking existing candidates against this job...
          </div>
        ) : (rediscoveryData?.candidates || []).length === 0 ? (
          <div className="mt-5 rounded-lg border border-dashed border-slate-200 bg-slate-50 p-5 text-sm leading-6 text-slate-600">
            No reusable candidates found yet. Import candidates for other jobs or general applicants, then this panel will surface possible matches automatically.
          </div>
        ) : (
          <>
            <div className="mt-5 grid gap-3 md:grid-cols-4">
              {[
                { label: "Pool checked", value: rediscoveryData?.total_pool_candidates || 0 },
                { label: "Possible matches", value: rediscoveryData?.available_matches || 0 },
                { label: "Strong matches", value: rediscoveryData?.strong_matches || 0 },
                { label: "Selected", value: selectedRediscoveryIds.length },
              ].map((item) => (
                <div key={item.label} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.label}</p>
                  <p className="mt-2 text-xl font-semibold text-slate-950">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">
                Select rediscovered candidates to add them to this job, then scan them with the normal Bulk Scan workflow.
              </p>
              <button
                type="button"
                onClick={toggleAllRediscoveryCandidates}
                className="text-left text-sm font-semibold text-emerald-700 transition hover:text-emerald-900"
              >
                {selectedRediscoveryIds.length === (rediscoveryData?.candidates || []).length ? "Clear selection" : "Select visible matches"}
              </button>
            </div>

            <div className="mt-4 grid gap-3 lg:grid-cols-2">
              {(rediscoveryData?.candidates || []).map((candidate) => {
                const isSelected = selectedRediscoveryIds.includes(candidate.candidate_id);

                return (
                  <button
                    key={candidate.candidate_id}
                    type="button"
                    onClick={() => toggleRediscoveryCandidate(candidate.candidate_id)}
                    className={`rounded-lg border p-4 text-left transition ${
                      isSelected
                        ? "border-emerald-400 bg-emerald-50 ring-2 ring-emerald-100"
                        : "border-slate-200 bg-white hover:border-emerald-200 hover:bg-emerald-50/40"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-950">{candidate.filename}</p>
                        <p className="mt-1 text-xs font-medium text-slate-500">
                          Imported {formatDate(candidate.imported_at)} via {candidate.source || "upload"}
                        </p>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2 py-1 text-xs font-semibold ${scoreClass(candidate.match_score)}`}>
                        {Math.round(candidate.match_score)}%
                      </span>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {(candidate.matching_skills || []).slice(0, 5).map((skill) => (
                        <span key={skill} className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                          {skill}
                        </span>
                      ))}
                      {(candidate.matching_skills || []).length === 0 && (
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">
                          Semantic match
                        </span>
                      )}
                    </div>

                    {(candidate.missing_skills || []).length > 0 && (
                      <p className="mt-3 line-clamp-2 text-xs leading-5 text-slate-500">
                        Gaps to verify: {(candidate.missing_skills || []).slice(0, 4).join(", ")}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Candidate Pipeline</h2>
            <p className="mt-1 text-sm text-slate-500">
              Summary grouped from existing scan data, shortlist status, and match score.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handleOpenRankings}
              aria-disabled={isFree}
              title={isFree ? "Available on Basic and Pro plans." : "Open job rankings."}
              className={`inline-flex min-h-[40px] items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold transition ${
                isFree
                  ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400"
                  : "bg-emerald-800 text-white hover:bg-emerald-900"
              }`}
            >
              <Star size={16} />
              Open Rankings
            </button>

            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Filter candidates"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100 md:w-64"
            />
          </div>
        </div>

        <div className="mt-6 grid gap-4 xl:grid-cols-3">
          {pipelineColumns.map((column) => (
            <div key={column.key} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="mb-4">
                <h3 className="font-semibold text-slate-950">{column.title}</h3>
                <p className="text-xs text-slate-500">{column.subtitle}</p>
              </div>

              <div className="space-y-3">
                {column.items.length === 0 ? (
                  <p className="rounded-md border border-dashed border-slate-200 bg-white p-4 text-sm text-slate-500">
                    No candidates yet.
                  </p>
                ) : (
                  column.items.slice(0, 6).map((candidate) => (
                    <div key={candidate.scan_id} className="rounded-md border border-slate-200 bg-white p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-950">{candidate.filename}</p>
                          <p className="mt-1 truncate text-xs text-slate-500">
                            {(candidate.matching_skills || []).slice(0, 3).join(", ") || "No skills detected"}
                          </p>
                          <p className="mt-1 text-xs font-medium text-slate-400">
                            Scanned {formatDate(candidate.created_at)}
                          </p>
                        </div>
                        <span className={`shrink-0 rounded-full border px-2 py-1 text-xs font-semibold ${scoreClass(candidate.match_score)}`}>
                          {Math.round(candidate.match_score)}%
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-slate-950">All Candidates</h2>
        <p className="mt-1 text-sm text-slate-500">
          Complete scan history for this job, ordered by strongest match.
        </p>

        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <th className="py-3 pr-4">Candidate</th>
                <th className="py-3 pr-4">Scanned</th>
                <th className="py-3 pr-4">Score</th>
                <th className="py-3 pr-4">Detected Skill Evidence</th>
                <th className="py-3 pr-4">Unconfirmed Skill Evidence</th>
                <th className="py-3 pr-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredCandidates.map((candidate) => (
                <tr key={candidate.scan_id} className="border-b border-slate-100 last:border-0">
                  <td className="max-w-[220px] py-3 pr-4 font-medium text-slate-950">
                    <span className="block truncate">{candidate.filename}</span>
                  </td>
                  <td className="py-3 pr-4 text-xs font-medium text-slate-500">
                    {formatDate(candidate.created_at)}
                  </td>
                  <td className="py-3 pr-4">
                    <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${scoreClass(candidate.match_score)}`}>
                      {Math.round(candidate.match_score)}%
                    </span>
                  </td>
                  <td className="max-w-[260px] py-3 pr-4 text-slate-600">
                    <span className="block truncate">{(candidate.matching_skills || []).join(", ") || "-"}</span>
                  </td>
                  <td className="max-w-[260px] py-3 pr-4 text-slate-600">
                    <span className="block truncate">{(candidate.missing_skills || []).join(", ") || "-"}</span>
                  </td>
                  <td className="py-3 pr-4">
                    {candidate.is_shortlisted ? (
                      <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
                        Shortlisted
                      </span>
                    ) : (
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                        Review
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );

  return (
    <DashboardLayout
      user={user}
      eyebrow="Job workspace"
      title={job.name || "Job Details"}
      subtitle="Review candidate pipeline, scan history, and job-level match quality"
      leftPanel={leftPanel}
      mainContent={mainContent}
      headerAction={
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            onClick={() => {
              if (bulkScanLocked) {
                toast.error(
                  isArchived
                    ? "Restore this job before scanning candidates."
                    : isFree
                    ? "Bulk Scan is available on Basic and Pro plans."
                    : "Your scan quota is finished for this billing cycle."
                );
                return;
              }

              navigate(`/bulk-scan?job_id=${jobId}`);
            }}
            aria-disabled={bulkScanLocked}
            className={`inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg px-5 text-sm font-semibold transition ${
              bulkScanLocked
                ? "cursor-not-allowed bg-slate-200 text-slate-400"
                : "bg-emerald-800 text-white hover:bg-emerald-900"
            }`}
          >
            <Search size={16} />
            Bulk Scan
          </button>
          <button
            onClick={handleArchiveToggle}
            disabled={updatingArchive}
            className={`inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg px-5 text-sm font-semibold transition disabled:opacity-50 ${
              isArchived
                ? "border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            {isArchived ? <RotateCcw size={16} /> : <Archive size={16} />}
            {updatingArchive ? "Updating..." : isArchived ? "Restore Job" : "Archive Job"}
          </button>
          <button
            onClick={handleResetScanResults}
            disabled={resettingScans}
            aria-disabled={!canResetScanResults}
            title={
              canResetScanResults
                ? "Clear rankings and scan history for this job. Scan quota is not restored."
                : "Available on Pro and Enterprise plans."
            }
            className={`inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg border px-5 text-sm font-semibold transition disabled:opacity-50 ${
              canResetScanResults
                ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
            }`}
          >
            <Trash2 size={16} />
            {resettingScans ? "Resetting..." : "Reset Results"}
          </button>
          <button
            onClick={handleOpenRankings}
            aria-disabled={isFree}
            title={isFree ? "Available on Basic and Pro plans." : "Open job rankings."}
            className={`inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg border px-5 text-sm font-semibold transition ${
              isFree
                ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <Star size={16} />
            Rankings
          </button>
          <button
            onClick={handleOpenCriteriaSimulator}
            aria-disabled={isFree || criteriaSimulatorLocked}
            title={isFree ? "Available on Basic and Pro plans." : criteriaSimulatorLocked ? "Run a scan for this job first." : "Open Criteria Match."}
            className={`inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg border px-5 text-sm font-semibold transition ${
              isFree || criteriaSimulatorLocked
                ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                : "border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
            }`}
          >
            <Target size={16} />
            Criteria Simulator
          </button>
          <button
            onClick={() => navigate("/dashboard_recruiter")}
            className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <ArrowLeft size={16} />
            Dashboard
          </button>
        </div>
      }
    />
  );
}
